import React, { useCallback } from 'react'
import AvatarEditor from 'react-avatar-editor'
import { InputRange } from '../forms/input/InputRange'

interface ImageEditorProps {
  image: string
  width?: number
  height?: number
  border?: number

  onFileChange?: (file: File) => void
}

export const ImageEditor: React.FC<ImageEditorProps> = ({
  image,
  width = 250,
  height = 250,
  border = 2,
  onFileChange
}) => {
  const [scale, setScale] = React.useState(1)
  const [rotation, setRotation] = React.useState(0)
  const [file, setFile] = React.useState<File | null>(null)
  const editorRef = React.useRef(null);
  const [timeInterval, setTimeInterval] = React.useState(null);

  React.useEffect(() => {
    if (file) {
      onFileChange?.(file)
    }
  }, [file, onFileChange])

  const handleScale = (e: React.ChangeEvent<HTMLInputElement>) => {
    const scale = parseFloat(e.target.value)
    setScale(scale)
    updateFile()
  }

  const handleRotation = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rotation = parseFloat(e.target.value)
    setRotation(rotation)
    updateFile()
  }

  const updateFile = useCallback(async () => {
    if (!image || !editorRef?.current) return null
    if (!editorRef.current) return null

    let blob: Blob | null = null

    try {
      const canvasScaled = editorRef.current?.getImage();
      const croppedImg = canvasScaled?.toDataURL();

      blob = await fetch(croppedImg)
          .then((res) => res.blob())
          .then((blob) => blob);
    } catch {

      blob = await fetch(image)
        .then((res) => res.blob())
        .then((blob) => blob);

    }

    const file = new File([blob], 'avatar.png', { type: 'image/png' });

    setFile(file)
  }, [image, editorRef])

  React.useEffect(() => {
    if (editorRef?.current) {
      updateFile()
    }
  }, [editorRef, updateFile])

  const updatePositionDebounced = React.useCallback(
    () => {
      clearTimeout(timeInterval);
      const newTimeout = setTimeout(updateFile, 200);
      setTimeInterval(newTimeout);
    }
  , [timeInterval, updateFile]);

    

  return (
    <div className="full-w flex justify-center items-center flex-column">
      <AvatarEditor
        image={image}
        width={width}
        height={height}
        border={border}
        color={[0, 0, 0, 1]} // RGBA
        scale={scale}
        padding={0}
        rotate={rotation}
        onPositionChange={updatePositionDebounced}
        ref={editorRef}
        crossOrigin="anonymous"
      ></AvatarEditor>
      <div className="flex gap-20 justify-space-between pt-40 pb-20 full-w">
        <InputRange
          label="Zoom"
          onChange={handleScale}
          min={1}
          max={5}
          step={0.5}
          value={scale}
        />
        <InputRange
          label="Rotation"
          onChange={handleRotation}
          min={0}
          max={180}
          step={1}
          value={rotation}
        />
      </div>
    </div>
  )
}